/**
 * Normalizers for the Class Schedule Staff/Student filter options.
 *
 * One helper per response so shape-guessing stays out of the component. Both
 * tolerate the response arriving as data.staff / data.students / staff / students
 * / data.data.*, and produce a stable { value, label } (+ searchable text).
 */

export interface FilterOption {
  value: number;
  label: string;
  search: string; // lowercased haystack for client-side search
}

const pickArray = (source: any, keys: string[]): any[] => {
  if (Array.isArray(source)) return source;
  for (const k of keys) {
    if (Array.isArray(source?.[k])) return source[k];
    if (Array.isArray(source?.data?.[k])) return source.data[k];
  }
  if (Array.isArray(source?.data)) return source.data;
  if (Array.isArray(source?.data?.data)) return source.data.data;
  return [];
};

const nameOf = (r: any): string =>
  (r?.name ||
    r?.full_name ||
    [r?.first_name ?? r?.teamFirstName, r?.last_name ?? r?.teamLastName].filter(Boolean).join(" ") ||
    "").trim();

export const normalizeStaffOptions = (response: any): FilterOption[] =>
  pickArray(response, ["staff"])
    .map((r) => {
      const name = nameOf(r) || `Staff #${r?.id}`;
      const subjects = Array.isArray(r?.subjects) ? r.subjects.join(" ") : "";
      // Haystack for the StaffMultiSelect search box — name + email +
      // subjects + optional location + role so clinicians can find a
      // teacher by the location they teach at or by their role title.
      // Any field missing on the response is simply omitted — the join
      // tolerates undefined without producing stray "undefined" tokens.
      const locationName =
        r?.location_name ?? r?.location?.name ?? r?.location?.location_name ?? "";
      const role = r?.role ?? r?.role_name ?? r?.user_role_name ?? r?.title ?? "";
      return {
        value: Number(r?.id),
        label: name,
        search: `${name} ${r?.email ?? ""} ${subjects} ${locationName} ${role}`.toLowerCase(),
      };
    })
    .filter((o) => Number.isFinite(o.value));

export const normalizeStudentOptions = (response: any): FilterOption[] =>
  pickArray(response, ["students"])
    .map((r) => {
      const name = nameOf(r) || `Student #${r?.id}`;
      const code = r?.student_code ?? r?.reference_id ?? "";
      return {
        value: Number(r?.id),
        label: code ? `${name} — ${code}` : name,
        search: `${name} ${code} ${r?.grade ?? ""} ${r?.guardian ?? ""}`.toLowerCase(),
      };
    })
    .filter((o) => Number.isFinite(o.value));

// ── Grade & Subject option normalizers ──────────────────────────────────────
// filter-options returns:
//   grades:         [{ id, name }]
//   grade_subjects: [{ id, name, grade_ids: [1,3,…] }]  ← always present, full map
//   subjects:       server-scoped variant (only when grade_ids[] is passed)
//
// The Class Schedule list uses `grade_subjects` so the Subject dropdown can
// react to the Grade selection with zero extra network calls.

export interface GradeOptionN { id: number; name: string; }
export interface SubjectOptionN { id: number; name: string; grade_ids: number[]; }

export const normalizeGradeOptions = (response: any): GradeOptionN[] =>
  pickArray(response, ["grades"])
    .map((g: any) => ({
      id: Number(g?.id),
      name: String(g?.name ?? g?.grade ?? "").trim(),
    }))
    .filter((g: GradeOptionN) => Number.isFinite(g.id) && g.name);

export const normalizeSubjectOptions = (response: any): SubjectOptionN[] =>
  pickArray(response, ["grade_subjects", "subjects"])
    .map((s: any) => ({
      id: Number(s?.id),
      name: String(s?.name ?? s?.subject_name ?? "").trim(),
      grade_ids: Array.isArray(s?.grade_ids)
        ? s.grade_ids.map((n: any) => Number(n)).filter((n: number) => Number.isFinite(n) && n > 0)
        : [],
    }))
    .filter((s: SubjectOptionN) => Number.isFinite(s.id) && s.name);

/**
 * Return the subjects available for the given grade selection.
 *   selectedGradeIds = [] → every subject (All Subjects)
 *   otherwise           → union of subjects mapped to any selected grade
 * Duplicates removed by id, ordered by name (stable, case-insensitive).
 */
export const subjectsForGrades = (
  subjects: SubjectOptionN[],
  selectedGradeIds: number[],
): SubjectOptionN[] => {
  if (!selectedGradeIds || selectedGradeIds.length === 0) return subjects;
  const wanted = new Set(selectedGradeIds.map(Number));
  const seen = new Set<number>();
  const out: SubjectOptionN[] = [];
  for (const s of subjects) {
    if (seen.has(s.id)) continue;
    if (s.grade_ids.some((g) => wanted.has(g))) {
      seen.add(s.id);
      out.push(s);
    }
  }
  return out.sort((a, b) => a.name.localeCompare(b.name));
};
